import { LEARNING_PLAN_UNIT_TYPE } from '../components/LearningPlan/LearningPlanUnit';
import { LearningPlan } from '../hydra';
import { QUESTION_TYPE } from '../components/Activity/Question';
import { CompanyProjectSubmission } from './teams';
import type { CmsModuleDefinition } from '../schemas/content-module';
import type { CmsProjectDefinition } from '../schemas/applied-project';
import type {
  CreateCmsAssessmentTestRequest,
  UpdateCmsAssessmentTestRequest,
} from '../schemas/assessment-test';
import {
  previewValidateAnswerRoute,
  type PreviewValidateAnswerBody,
  type PreviewValidateAnswerResponse,
} from '@enki/contracts/admin/preview-validate-answer';
import {
  getCmsModuleRoute,
  getCmsModulesRoute,
  type CmsModuleListItem,
  type GetCmsModuleResponse,
  type GetCmsModulesResponse,
} from '@enki/contracts/admin/cms/get-modules';
import {
  getCmsProjectRoute,
  getCmsProjectsRoute,
  type CmsProjectListItem,
  type GetCmsProjectResponse,
  type GetCmsProjectsResponse,
} from '@enki/contracts/admin/cms/get-projects';
import {
  getCmsAssessmentTestRoute,
  getCmsAssessmentTestsRoute,
  type GetCmsAssessmentTestResponse,
  type GetCmsAssessmentTestsResponse,
} from '@enki/contracts/admin/cms/get-assessment-tests';
import { requestContract } from './contract-client';
import {
  postCommentProjectSubmissionRoute,
  reviewSubmissionRoute,
  saveOfficeHoursNotesRoute,
} from '@enki/contracts/admin/submission-writes';
import {
  addCompanyRoute,
  updateCompanyRoute,
  addLearnersRoute,
  editLearningPlansRoute,
  assignMentorToCompanyRoute,
  createMentorRoute,
} from '@enki/contracts/admin/company-writes';
import {
  getCompaniesRoute,
  getCompanyRoute,
  getCompanyOfficeHoursRoute,
  getCompanyAssessmentTestsRoute,
  getAssessmentTestSessionRoute,
  getExerciseSubmissionsRoute,
  getCompanyProjectSubmissionsRoute,
} from '@enki/contracts/admin/company-reads';
import {
  createCmsModuleRoute,
  updateCmsModuleRoute,
  type CreateCmsModuleResponse as ContractCreateCmsModuleResponse,
  type UpdateCmsModuleResponse as ContractUpdateCmsModuleResponse,
} from '@enki/contracts/admin/cms/write-modules';
import {
  createCmsProjectRoute,
  updateCmsProjectRoute,
  type CreateCmsProjectResponse as ContractCreateCmsProjectResponse,
  type UpdateCmsProjectResponse as ContractUpdateCmsProjectResponse,
} from '@enki/contracts/admin/cms/write-projects';
import {
  createCmsAssessmentTestRoute,
  updateCmsAssessmentTestRoute,
  type CreateCmsAssessmentTestResponse as ContractCreateCmsAssessmentTestResponse,
  type UpdateCmsAssessmentTestResponse as ContractUpdateCmsAssessmentTestResponse,
} from '@enki/contracts/admin/cms/write-assessment-tests';
import {
  addCohortRoute,
  inviteLearnersToCohortRoute,
  assignLearnersToCohortMentorRoute,
  updateMentorCohortAvailabilityRoute,
  addAssessmentTestCohortRoute,
  inviteUsersToAssessmentTestsRoute,
} from '@enki/contracts/admin/cohort-writes';

export type CompaniesResponse = {
  items: Array<{
    id: number;
    name: string;
    slug: string;
    latestCohortStartDate: string | null;
    latestCohortEndDate: string | null;
    numLearners: number;
  }>;
};

export async function getCompanies(token: string) {
  return requestContract(getCompaniesRoute, { token }) as Promise<CompaniesResponse>;
}

export async function addCompany(
  token: string,
  {
    name,
    slug,
    isKickOffEnabled,
    isCompanyProjectUploadsEnabled,
    isRetroSurveyEnabled,
    isCohortWelcomeEmailEnabled,
    isOfficeHoursRemindersEnabled,
    isLearningPlanNotificationsEnabled,
    isAssessmentTestEnabled,
    logoUrl,
  }: {
    name: string;
    slug: string;
    isKickOffEnabled?: boolean;
    isCompanyProjectUploadsEnabled?: boolean;
    isRetroSurveyEnabled?: boolean;
    isCohortWelcomeEmailEnabled?: boolean;
    isOfficeHoursRemindersEnabled?: boolean;
    isLearningPlanNotificationsEnabled?: boolean;
    isAssessmentTestEnabled?: boolean;
    logoUrl: string | null;
  }
) {
  return requestContract(addCompanyRoute, { token, body: {
      name,
      slug,
      isKickOffEnabled,
      isCompanyProjectUploadsEnabled,
      isRetroSurveyEnabled,
      isCohortWelcomeEmailEnabled,
      isOfficeHoursRemindersEnabled,
      isLearningPlanNotificationsEnabled,
      isAssessmentTestEnabled,
      logoUrl,
    } });
}

export async function updateCompany(
  token: string,
  {
    companyId,
    isKickOffEnabled,
    isCompanyProjectUploadsEnabled,
    isRetroSurveyEnabled,
    isCohortWelcomeEmailEnabled,
    isOfficeHoursRemindersEnabled,
    isLearningPlanNotificationsEnabled,
    isAssessmentTestEnabled,
    logoUrl,
  }: {
    companyId: number | string;
    isKickOffEnabled: boolean;
    isCompanyProjectUploadsEnabled: boolean;
    isRetroSurveyEnabled: boolean;
    isCohortWelcomeEmailEnabled: boolean;
    isOfficeHoursRemindersEnabled: boolean;
    isLearningPlanNotificationsEnabled: boolean;
    isAssessmentTestEnabled: boolean;
    logoUrl: string | null;
  }
) {
  return requestContract(updateCompanyRoute, { token, params: { companyId: Number(companyId) }, body: {
      isKickOffEnabled,
      isCompanyProjectUploadsEnabled,
      isRetroSurveyEnabled,
      isCohortWelcomeEmailEnabled,
      isOfficeHoursRemindersEnabled,
      isLearningPlanNotificationsEnabled,
      isAssessmentTestEnabled,
      logoUrl,
    } });
}

export enum LEARNER_STATUS {
  INVITED = 'Invited',
  ONBOARDED = 'Onboarded',
  ACTIVE = 'Active',
  INACTIVE = 'Inactive',
  INVITE_EXPIRED = 'Invite Expired',
  COHORT_ENDED = 'Cohort Ended',
}

export type LearningPlanModule = {
  content: {
    id: number;
    title: string;
    slug: string;
  };
  topic: string;
  topicIconUrl: string;
  type: LEARNING_PLAN_UNIT_TYPE.CONTENT_MODULE;
  coreLessonsCount: number | null;
  coreSubmittedLessonsCount: number | null;
  stretchLessonsCount: number | null;
  stretchSubmittedLessonsCount: number | null;
  companyName: string | null;
};

export type LearningPlanProject = {
  projectId: number;
  topic: string;
  type: LEARNING_PLAN_UNIT_TYPE.PROJECT;
  topicIconUrl: string;
  title: string;
  exercisesCount: number | null;
  submittedExercisesCount: number | null;
  slug: string;
  companyName: string | null;
};

export type LearningPlanItem = LearningPlanModule | LearningPlanProject;

export type CompanyResponse = {
  name: string;
  slug: string;
  createdAt: Date;
  logoUrl: string | null;
  settings: {
    KICK_OFF: boolean;
    WELCOME_EMAIL: boolean;
    RETRO_SURVEY: boolean;
    COMPANY_PROJECT_UPLOAD: boolean;
    OFFICE_HOURS_REMINDERS: boolean;
    LEARNING_PLAN_NOTIFICATIONS: boolean;
    ASSESSMENT_TEST: boolean;
  };
  learners: Array<{
    id: number;
    name: string;
    email: string;
    addedAt: Date;
    // lastActiveAt: Date | null;
    hasAccess: boolean;
    learningPlan: Array<LearningPlanModule | LearningPlanProject> | null;
    onboardingAnswers: {
      role: string | null;
    };
  }>;
  cohorts: Array<{
    cycleId: number;
    startDate: Date;
    endDate: Date;
    isOpen: boolean;
    kickOffTimes: Date[] | null;
    officeHourTimes: Date[] | null;
    learners: Array<{
      sessionId: number;
      learnerId: number;
      lastActiveAt: Date | null;
      name: string;
      email: string;
      status: LEARNER_STATUS;
      invitedAt: Date | null;
      onboardedAt: Date | null;
      onboardingAnswers: {
        timezone: string | null;
        localNotificationTime: string | null;
        kickOffTime: Date | null;
        officeHoursTimes: Date[];
        commonTasks: string | null;
        additionalComments: string | null;
        skillRatings: Record<string, number>;
      };
      mentor: {
        profileId: number;
        name: string;
        email: string;
        officeHourGroupMemberId: number | null;
        officeHourGroupId: number | null;
      } | null;
      progress: {
        mentor: {
          numAIQuestions: number;
          numAIHintInteractions: number;
          numAIWhatDoesThisMean: number;
          numMentorSOSQuestions: number;
        };
        general: {
          numDaysActive: number;
          numExercisesCompleted: number;
          totalExerciseTime: number | null;
        };
        difficulty: {
          average: number | null;
          items: Array<{
            name: string;
            type: string; // 'PROJECT' | 'ACTIVITY'
            difficultyRating: number | null;
          }>;
        };
        submissions: Array<{
          submissionId: number;
          name: string;
          type: string;
          submissionDate: Date;
        }>;
        topics: Array<{
          name: string;
          iconUrl: string;
          units: Array<LearningPlanModule | LearningPlanProject>;
        }>;
        numCoreAttemptedActivities: number;
        numStretchAttemptedActivities: number;
        numAttemptedProjects: number;
      };
    }>;
    mentors: Array<{
      profileId: number;
      name: string;
      email: string;
      officeHoursAvailability: Date[] | null;
    }>;
  }>;
  modules: Array<{
    contentId: number;
    contentTitle: string;
    contentSlug: string;
    createdAt: Date;
    topicIconUrl: string;
    topic: string;
    isDeprecated: boolean;
    isDraft: boolean;
    coreActivitiesCount: number;
    stretchActivitiesCount: number;
    companyName: string | null;
  }>;
  projects: Array<{
    projectId: number;
    topic: string;
    slug: string;
    topicIconUrl: string;
    title: string;
    isDraft: boolean;
    exercisesCount: number;
    companyName: string | null;
  }>;
  mentors: Array<{
    id: number;
    name: string;
    email: string;
    companyMentorId: number | null;
    topics: Array<string>;
    isCompanyMentor: boolean;
  }>;
};

export async function getCompany(token: string, id: number) {
  return requestContract(getCompanyRoute, {
    token,
    params: { companyId: id },
  }) as Promise<CompanyResponse>;
}

export async function addLearners(
  token: string,
  { learners, id }: { learners: { name: string; email: string }[]; id: string }
) {
  return requestContract(addLearnersRoute, { token, params: { companyId: Number(id) }, body: { learners } });
}

export async function addCohort(
  token: string,
  {
    companyId,
    startDate,
    endDate,
    kickOffTimes,
    officeHourTimes,
  }: {
    companyId: string;
    startDate: string;
    endDate: string;
    kickOffTimes: string[];
    officeHourTimes: string[];
  }
) {
  return requestContract(addCohortRoute, { token, params: { companyId: Number(companyId) }, body: { startDate, endDate, kickOffTimes, officeHourTimes } });
}

export async function inviteLearnersToCohort(
  token: string,
  {
    companyId,
    cohortId,
    learnerIds,
  }: {
    companyId: number;
    cohortId: number;
    learnerIds: number[];
  }
) {
  return requestContract(inviteLearnersToCohortRoute, { token, params: { companyId, cohortId }, body: { learnerIds } });
}

export type AssignLearnersToCohortMentorParams = {
  companyId: number;
  cohortId: number;
  assignments: {
    mentorProfileId: number;
    firstOfficeHourTimestamp: Date;
    cohortInviteIds: number[];
  }[];
};

export async function assignLearnersToCohortMentor(
  token: string,
  { companyId, cohortId, assignments }: AssignLearnersToCohortMentorParams
) {
  return requestContract(assignLearnersToCohortMentorRoute, { token, params: { companyId, cohortId }, body: { assignments: assignments.map((assignment) => ({ ...assignment, firstOfficeHourTimestamp: assignment.firstOfficeHourTimestamp.toISOString() })) } });
}

export async function editLearningPlans(
  token: string,
  {
    companyId,
    learners,
  }: {
    companyId: number;
    learners: { id: number; learningPlan: LearningPlan }[];
  }
) {
  return requestContract(editLearningPlansRoute, { token, params: { companyId }, body: { learners } });
}

export async function assignMentorToCompany(
  token: string,
  {
    companyId,
    mentorProfileId,
  }: {
    companyId: number | string;
    mentorProfileId: number;
  }
) {
  return requestContract(assignMentorToCompanyRoute, { token, params: { companyId: Number(companyId) }, body: { mentorProfileId } });
}

export async function createMentor(
  token: string,
  {
    name,
    email,
  }: {
    name: string;
    email: string;
  }
) {
  return requestContract(createMentorRoute, { token, body: { name, email } });
}

export enum UNIT_TYPE {
  PROJECT = 'PROJECT',
  ACTIVITY = 'ACTIVITY',
}

export type SubmissionResponse = {
  submissions: Array<{
    learner: {
      id: number;
      name: string;
      email: string;
    };
    submission: {
      id: number;
      didRequestMentorReview: boolean;
      mentorReview: string;
      isReviewed: boolean;
      requestedAt: string | null;
      learnerComment: string | null;
      learnerAnswer: string;
      repliedBy: {
        id: number;
        name: string;
        email: string;
      } | null;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      fullAnswerStates: Record<string, any>;
    };
    unit:
      | {
          type: UNIT_TYPE.PROJECT;
          title: string;
          topic: string;
          iconUrl: string;
          projectSlug: string;
        }
      | {
          type: UNIT_TYPE.ACTIVITY;
          title: string;
          topic: string;
          iconUrl: string;
          activitySlug: string;
          missionSlug: string;
        };
    question: {
      type: QUESTION_TYPE;
      id: number;
      index: number;
      text: string;
      mentorNotes: string;
      modelAnswer: string;
      aiReview: string | null;
    };
  }>;
};

export type CompanyProjectSubmissionResponse = {
  submissions: Array<{
    learner: {
      id: number;
      name: string;
      email: string;
    };
    cohort: {
      id: number;
      startDate: Date;
      endDate: Date;
    };
    submissionId: number;
    PROJECT_CHECKIN_1: {
      isLocked: boolean;
      unlockedAt: Date | null;
      isCompleted: boolean;
      submittedAt: Date | null;
      survey: Array<{
        question: string;
        answer: string;
      }>;
      notifications: Array<{
        type: string;
        sent_at: Date;
      }>;
    } | null;
    PROJECT_CHECKIN_2: {
      isLocked: boolean;
      unlockedAt: Date | null;
      isCompleted: boolean;
      submittedAt: Date | null;
      survey: CompanyProjectSubmission['survey'];
      notifications: Array<{
        type: string;
        sent_at: Date;
      }>;
    } | null;
    PROJECT_UPLOAD: {
      isLocked: boolean;
      unlockedAt: Date | null;
      isCompleted: boolean;
      submittedAt: Date | null;
      survey: CompanyProjectSubmission['survey'];
      file_links: Array<string> | null;
      external_links: Array<string> | null;
      notifications: Array<{
        type: string;
        sent_at: Date;
      }>;
    } | null;
    comment: {
      comment: string;
      commented_at: string | null; // ISO 8601 timestamp
      commented_by: {
        user_id: number;
        name: string;
        email: string;
      } | null;
    } | null;
  }>;
};

export async function getExerciseSubmissions(
  token: string,
  { companyId }: { companyId: number }
) {
  return requestContract(getExerciseSubmissionsRoute, {
    token,
    params: { companyId },
  }) as Promise<SubmissionResponse>;
}

export async function getCompanyProjectSubmissions(
  token: string,
  { companyId }: { companyId: number }
) {
  return requestContract(getCompanyProjectSubmissionsRoute, {
    token,
    params: { companyId },
  }) as Promise<CompanyProjectSubmissionResponse>;
}

export async function reviewSubmission(
  token: string,
  {
    companyId,
    submissionId,
    type,
    review,
    questionId,
  }: {
    companyId: number;
    submissionId: number;
    type: 'ACTIVITY' | 'PROJECT';
    review: string;
    questionId: number;
  }
) {
  return requestContract(reviewSubmissionRoute, {
    token,
    params: { companyId },
    body: {
      submissionId,
      type,
      review,
      questionId,
    },
  });
}

export async function saveOfficeHoursNotes(
  token: string,
  {
    companyId,
    learners,
  }: {
    companyId: number;
    learners: {
      offficeHourNoteId: number;
      didAttend: boolean;
      notes: {
        goals: string;
        challenges: string;
        insights: string;
      };
    }[];
  }
) {
  return requestContract(saveOfficeHoursNotesRoute, {
    token,
    params: { companyId },
    body: { learners },
  });
}

export async function postCommentProjectSubmission(
  token: string,
  {
    companyId,
    submissionId,
    comment,
  }: { companyId: number; submissionId: number; comment: string }
) {
  return requestContract(postCommentProjectSubmissionRoute, {
    token,
    params: { companyId },
    body: { submissionId, comment },
  });
}

export type OfficeHoursResponse = {
  officeHoursGroups: Array<{
    id: number;
    calendarEventId: string | null;
    mentor: {
      mentorProfileId: number;
      mentorUserId: number;
      name: string;
      email: string;
    };
    cohort: {
      id: number;
      startDate: Date;
      endDate: Date;
    };
    members: Array<{
      id: number;
      name: string;
      learnerId: number;
      email: string;
      noteSummary: string | null;
      notesLastUpdatedAt: Date | null;
    }>;
    officeHours: Array<{
      officeHourId: number;
      timestamp: Date;
      members: Array<{
        officeHourNotesId: number;
        memberId: number;
        didAttend: boolean | null;
        notes: {
          goals: string;
          challenges: string;
          insights: string;
        };
      }>;
    }>;
  }>;
};

export async function getCompanyOfficeHours(
  token: string,
  { companyId }: { companyId: number }
) {
  return requestContract(getCompanyOfficeHoursRoute, {
    token,
    params: { companyId },
  }) as Promise<OfficeHoursResponse>;
}

export async function updateMentorCohortAvailability(
  token: string,
  {
    companyId,
    cohortId,
    availabilities,
  }: {
    companyId: number;
    cohortId: number;
    availabilities: {
      companyMentorId: number | null;
      officeHourTimes: string[];
    }[];
  }
) {
  return requestContract(updateMentorCohortAvailabilityRoute, { token, params: { companyId, cohortId }, body: { availabilities } });
}

export type AssessmentTestSection = {
  id: string;
  content: string;
  template: string;
  followUpContent?: string | null;
  followUpPlaceholder?: string | null;
} & (
  | {
      type: 'text-input';
      placeholder: string | null;
    }
  | {
      type: 'ranking';
      options: Array<{
        id: string;
        content: string;
      }>;
    }
  | {
      type: 'multiple-choice';
      isSingleChoice: boolean;
      options: Array<{
        id: string;
        content: string;
      }>;
    }
  | {
      type: 'matching';
      leftItems: Array<{
        id: string;
        content: string;
      }>;
      rightItems: Array<{
        id: string;
        content: string;
      }>;
    }
  | {
      type: 'fill-in-the-gap';
      template: string;
      blanks: Array<{
        id: string;
        content: string;
        placeholder: string;
      }>;
    }
  | {
      type: 'py-playground';
      setupCode: string;
      startingCode: string;
    }
  | {
      type: 'llm-playground';
      initialPrompt: string;
    }
  | {
      type: 'multiple-rating';
      questions: Array<{
        id: string;
        content: string;
        options: Array<string>;
      }>;
    }
  | {
      type: 'py-editor';
      startingCode: string;
    }
  | {
      type: 'llm-editor';
      initialPrompt: string;
    }
  | {
      type: 'text-template';
      template: string;
      blanks: Array<{
        id: string;
        content: string;
        placeholder: string;
      }>;
    }
  | {
      type: 'matrix-ranking';
      items: Array<{ id: string; content: string }>;
      horizontalAxisLabel: string;
      verticalAxisLabel: string;
      annotationPrompt: string;
    }
);

export type AssessmentTestStep = {
  id: string;
  title: string;
  label: string;
  introduction: string | null;
  sections: Array<AssessmentTestSection>;
  objectives: Array<{
    id: string;
    description: string;
    facets: Array<{
      questions: string[];
      description: string;
    }>;
  }>;
};

export type AssessmentTestStepAnswer = {
  id: string;
  introduction: {
    timeIntervals: Array<{ startedAt: Date; endedAt: Date | null }>;
  } | null;
  sections: Array<
    {
      id: string;
      timeIntervals: Array<{ startedAt: Date; endedAt: Date | null }>;
      didInteract: boolean;
      isAnswered: boolean;
      followUpAnswer: string | null;
    } & (
      | {
          type: 'text-input';
          answer: string;
        }
      | {
          type: 'ranking';
          answer: Array<{
            id: string;
            content: string;
          }>;
        }
      | {
          type: 'multiple-choice';
          answer: Array<{
            id: string;
            content: string;
          }>;
        }
      | {
          type: 'matching';
          answer: Array<{
            leftId: string;
            rightId: string;
          }>;
        }
      | {
          type: 'fill-in-the-gap';
          answer: Array<{
            id: string;
            content: string;
          }>;
        }
      | {
          type: 'py-playground';
          answer: {
            output: string;
            code: string;
          };
        }
      | {
          type: 'llm-playground';
          answer: {
            prompt: string;
            output: string;
          };
        }
      | {
          type: 'multiple-rating';
          answer: Array<{
            id: string;
            label: string;
          }>;
        }
      | {
          type: 'py-editor';
          answer: string;
        }
      | {
          type: 'llm-editor';
          answer: string;
        }
      | {
          type: 'text-template';
          answer: Array<{
            id: string;
            content: string;
          }>;
        }
      | {
          type: 'matrix-ranking';
          answer: Array<{
            item: string;
            x: number;
            y: number;
            note: string;
          }>;
        }
    )
  >;
};

export type AssessmentTestResult = {
  skillScores: Array<{
    skill: string;
    skillName: string;
    score: number; // 1-10 aggregate score
    confidence: number;
    // Objective breakdown for transparent aggregation (counts and IDs can be derived from these lists)
    objectivesByStatus: {
      exceeded: Array<{
        id: string;
        missedFacets: string[];
        satisfiedFacets: string[];
      }>;
      met: Array<{
        id: string;
        missedFacets: string[];
        satisfiedFacets: string[];
      }>;
      partial: Array<{
        id: string;
        missedFacets: string[];
        satisfiedFacets: string[];
      }>;
      notMet: Array<{
        id: string;
        missedFacets: string[];
        satisfiedFacets: string[];
      }>;
      notTested: Array<{
        id: string;
        missedFacets: string[];
        satisfiedFacets: string[];
      }>;
    };
    keyStrengths: string; // markdown string
    developmentAreas: string; // markdown string
    aggregationReasoning: string; // Why this score represents the skill level
    comparativeLevel:
      | 'novice'
      | 'developing'
      | 'competent'
      | 'proficient'
      | 'expert';
    levelJustification: string; // Detailed reasoning for level assignment
  }>;
  roleProfile: {
    primaryRole: 'architect' | 'engineer';
    roleIndicators: {
      architecturalThinking: number; // 1-10
      systemsDesign: number;
      strategicPlanning: number;
      stakeholderManagement: number;
      technicalDepth: number;
      implementationFocus: number;
      operationalConcerns: number;
      detailOrientation: number;
    };
    roleReasoning: string; // Comprehensive explanation of role assignment
    evidenceFromResponses: string[]; // Specific examples supporting this role
  };
  learningRecommendations: {
    reasoning: string;
    explanation: string;
    learningPlan: Array<{
      type: 'module' | 'project';
      slug: string;
      title: string;
      levels: ('intro' | 'practice' | 'advanced')[];
      objectives: Array<{
        objectiveId: string;
        description: string;
        skillId: string;
      }>;
      reasoning: string;
      explanation: string;
      unit: {
        title: string;
        iconUrl: string;
      } | null;
    }>;
  };
  overallSummary: {
    keyStrengthsShort: string; // plain text string
    priorityDevelopmentAreasShort: string; // plain text string
    readinessReasoningShort: string; // plain text string
    confidence: number;
    readinessScore: number; // 1-10 overall AI readiness
    readinessLevel:
      | 'entry'
      | 'developing'
      | 'competent'
      | 'advanced'
      | 'expert';
    readinessReasoning: string;
    keyStrengths: string; // markdown string
    priorityDevelopmentAreas: string; // markdown string
  };
  crossScenarioPatterns: {
    behavioralPatterns: string; // markdown string
    behavioralPatternsShort: string; // markdown string
    cognitiveApproach: string; // markdown string
    cognitiveApproachShort: string; // markdown string
  };
  selfAssessmentCalibration: {
    overallAlignment:
      | 'well_calibrated'
      | 'overconfident'
      | 'underconfident'
      | 'inconsistent';
    calibrationSummary: string;
    calibrationEvidence: string[];
    calibrationImplications: string[];
    skillCalibrations: Array<{
      skill: string;
      skillName: string;
      calibrationLabel:
        | 'well_calibrated'
        | 'overconfident'
        | 'underconfident'
        | 'inconsistent'
        | 'not_applicable';
      analysis: string;
    }>;
  };
  scenarioEvaluations: Array<{
    insights: string;
    scenarioId: string;
    scenarioTitle: string;
    answersMarkdown: string;
    overallPerformance: {
      score: number;
      reasoning: string;
      keyStrengths: string;
      developmentAreas: string;
    };
    objectiveEvaluations: Array<{
      score: number;
      skill: string; // e.g., 'reqs-gathering' | 'genai' | 'aws' | 'mlops' | 'py'
      status: 'exceeded' | 'met' | 'partial' | 'not_met' | 'not_tested';
      objectiveId: string;
      bonusSignals: string;
      missedFacets: string[];
      satisfiedFacets: string[];
      comparativeLevel:
        | 'novice'
        | 'developing'
        | 'competent'
        | 'proficient'
        | 'expert'
        | 'not_tested';
      elicitableFacets: string[];
      evidenceAnalysis: string;
      decisionRationale: string;
      objectiveDescription: string;
      selfAssessmentAlignment:
        | 'aligned'
        | 'overconfident'
        | 'underconfident'
        | 'not_applicable';
    }>;
  }>;
};

export type MinimalAssessmentTestResult = {
  skillScores: Array<{
    skill: string;
    skillName: string;
    score: number;
    confidence: number;
    comparativeLevel:
      | 'novice'
      | 'developing'
      | 'competent'
      | 'proficient'
      | 'expert';
  }>;
  overallSummary: {
    confidence: number;
    readinessScore: number;
    readinessLevel:
      | 'entry'
      | 'developing'
      | 'competent'
      | 'advanced'
      | 'expert';
  };
};

export type AssessmentTestResponse = {
  teamAverage: {
    totalScore: number;
    skillAverages: Array<{
      skill: string;
      score: number;
    }>;
  } | null;
  cohorts: Array<{
    name: string;
    id: number;
    createdAt: Date;
    cohortAverage: {
      totalScore: number;
      skillAverages: Array<{
        skill: string;
        score: number;
      }>;
    } | null;
  }>;
  tests: Array<{
    name: string;
    assessmentTestId: number;
  }>;
  testAssignments: Array<{
    name: string;
    assessmentTestId: number;
    assessmentTestSessionId: number;
    dimensions: Array<{
      id: string;
      name: string;
      label: string;
      iconUrl: string;
    }>;
    invitedAt: Date;
    startedAt: Date | null;
    finishedAt: Date | null;
    finishReason: 'COMPLETED' | 'TIME_EXPIRED';
    reviewedAt: Date | null;
    results: MinimalAssessmentTestResult | null;
    cohort: {
      name: string;
      id: number;
    };
    learner: {
      id: number;
    };
    user: {
      id: number;
      name: string;
      email: string;
    };
    timeSpent: number | null;
    teamAverage: {
      totalScore: number;
      skillAverages: Array<{
        skill: string;
        score: number;
      }>;
      count: number;
    } | null;
    cohortAverage: {
      totalScore: number;
      skillAverages: Array<{
        skill: string;
        score: number;
      }>;
      count: number;
    } | null;
  }>;
};

export type TestAssignment = AssessmentTestResponse['testAssignments'][number];

export type AssessmentTestSessionDetailResponse = {
  name: string;
  assessmentTestId: number;
  assessmentTestSessionId: number;
  steps: Array<AssessmentTestStep>;
  answers: Array<AssessmentTestStepAnswer>;
  dimensions: Array<{
    id: string;
    name: string;
    label: string;
    iconUrl: string;
  }>;
  invitedAt: Date;
  startedAt: Date | null;
  finishedAt: Date | null;
  finishReason: 'COMPLETED' | 'TIME_EXPIRED';
  reviewedAt: Date | null;
  results: AssessmentTestResult | null;
  cohort: {
    name: string;
    id: number;
  };
  user: {
    id: number;
    name: string;
    email: string;
  };
  testerId: number;
  timeSpent: number | null;
  teamAverage: {
    totalScore: number;
    skillAverages: Array<{
      skill: string;
      score: number;
    }>;
    count: number;
  } | null;
  cohortAverage: {
    totalScore: number;
    skillAverages: Array<{
      skill: string;
      score: number;
    }>;
    count: number;
  } | null;
};

export async function getAssessmentTests(
  token: string,
  { companyId }: { companyId: number }
) {
  return requestContract(getCompanyAssessmentTestsRoute, {
    token,
    params: { companyId },
  }) as Promise<AssessmentTestResponse>;
}

export async function getAssessmentTestSession(
  token: string,
  {
    companyId,
    assessmentTestSessionId,
  }: {
    companyId: number;
    assessmentTestSessionId: number;
  }
) {
  return requestContract(getAssessmentTestSessionRoute, {
    token,
    params: { companyId, assessmentTestSessionId },
  }) as Promise<AssessmentTestSessionDetailResponse>;
}

export async function inviteUsersToAssessmentTests(
  token: string,
  {
    companyId,
    cohortId,
    testAssignments,
  }: {
    companyId: number;
    cohortId: number;
    testAssignments: {
      name: string;
      email: string;
      assessmentTestId: number;
    }[];
  }
) {
  return requestContract(inviteUsersToAssessmentTestsRoute, { token, params: { companyId }, body: { testAssignments, cohortId } });
}

export async function addAssessmentTestCohort(
  token: string,
  { companyId, name }: { companyId: number; name: string }
) {
  return requestContract(addAssessmentTestCohortRoute, { token, params: { companyId }, body: { name } });
}

export type PreviewValidateAnswerRequest = PreviewValidateAnswerBody;
export type { PreviewValidateAnswerResponse };

export async function previewValidateAnswer(
  token: string,
  data: PreviewValidateAnswerRequest
): Promise<PreviewValidateAnswerResponse> {
  return requestContract(previewValidateAnswerRoute, {
    token,
    body: data,
  });
}

// ── CMS modules ──────────────────────────────────────────────────────────────
// CMS module endpoints. The list is a lightweight row; get/create/update carry
// the full module definition.
export type {
  CmsModuleListItem,
  GetCmsModulesResponse,
  GetCmsModuleResponse,
};
export type CreateCmsModuleRequest = CmsModuleDefinition;
export type CreateCmsModuleResponse = ContractCreateCmsModuleResponse;
export type UpdateCmsModuleRequest = Omit<CmsModuleDefinition, 'slug'>;
export type UpdateCmsModuleResponse = ContractUpdateCmsModuleResponse;

export async function getCmsModules(
  token: string
): Promise<GetCmsModulesResponse> {
  return requestContract(getCmsModulesRoute, { token });
}

export async function getCmsModule(
  token: string,
  slug: string
): Promise<GetCmsModuleResponse> {
  return requestContract(getCmsModuleRoute, { token, params: { slug } });
}

export async function createCmsModule(
  token: string,
  data: CreateCmsModuleRequest
): Promise<CreateCmsModuleResponse> {
  return requestContract(createCmsModuleRoute, { token, body: data });
}

export async function updateCmsModule(
  token: string,
  slug: string,
  data: UpdateCmsModuleRequest
): Promise<UpdateCmsModuleResponse> {
  return requestContract(updateCmsModuleRoute, { token, params: { slug }, body: data });
}

// ── CMS projects ─────────────────────────────────────────────────────────────
// CMS project endpoints.
export type {
  CmsProjectListItem,
  GetCmsProjectsResponse,
  GetCmsProjectResponse,
};
export type CreateCmsProjectRequest = CmsProjectDefinition;
export type CreateCmsProjectResponse = ContractCreateCmsProjectResponse;
export type UpdateCmsProjectRequest = Omit<CmsProjectDefinition, 'slug'>;
export type UpdateCmsProjectResponse = ContractUpdateCmsProjectResponse;

export async function getCmsProjects(
  token: string
): Promise<GetCmsProjectsResponse> {
  return requestContract(getCmsProjectsRoute, { token });
}

export async function getCmsProject(
  token: string,
  slug: string
): Promise<GetCmsProjectResponse> {
  return requestContract(getCmsProjectRoute, { token, params: { slug } });
}

export async function createCmsProject(
  token: string,
  data: CreateCmsProjectRequest
): Promise<CreateCmsProjectResponse> {
  return requestContract(createCmsProjectRoute, { token, body: data });
}

export async function updateCmsProject(
  token: string,
  slug: string,
  data: UpdateCmsProjectRequest
): Promise<UpdateCmsProjectResponse> {
  return requestContract(updateCmsProjectRoute, { token, params: { slug }, body: data });
}

// ── CMS assessment tests ─────────────────────────────────────────────────────
// CMS assessment-test endpoints. The list returns full definitions.
export type { GetCmsAssessmentTestsResponse, GetCmsAssessmentTestResponse };
export type CreateCmsAssessmentTestResponse = ContractCreateCmsAssessmentTestResponse;
export type UpdateCmsAssessmentTestResponse = ContractUpdateCmsAssessmentTestResponse;

export async function getCmsAssessmentTests(
  token: string
): Promise<GetCmsAssessmentTestsResponse> {
  return requestContract(getCmsAssessmentTestsRoute, { token });
}

export async function getCmsAssessmentTest(
  token: string,
  slug: string
): Promise<GetCmsAssessmentTestResponse> {
  return requestContract(getCmsAssessmentTestRoute, {
    token,
    params: { slug },
  });
}

export async function createCmsAssessmentTest(
  token: string,
  data: CreateCmsAssessmentTestRequest
): Promise<CreateCmsAssessmentTestResponse> {
  return requestContract(createCmsAssessmentTestRoute, { token, body: data });
}

export async function updateCmsAssessmentTest(
  token: string,
  slug: string,
  data: UpdateCmsAssessmentTestRequest
): Promise<UpdateCmsAssessmentTestResponse> {
  return requestContract(updateCmsAssessmentTestRoute, { token, params: { slug }, body: data });
}
